import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertCircle, ArrowLeft, ClipboardList, ListFilter, Search, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from 'components/ui/dialog';
import { TableSearch } from 'components/ui/data-table';
import { FilterButton, FilterReset, type FilterOption } from 'components/ui/filter-button';
import { TablePager, LoadMore } from 'components/ui/pagination';
import Skeleton from 'components/ui/skeleton';
import EmptyState from 'components/ui/EmptyState';
import { useToast } from 'components/ui/use-toast';
import { useIsMobile } from 'hooks/use-media-query';
import { cn } from 'lib/utils';
import * as TestModule from 'modules/test';
import { usePublicTestCategory } from 'modules/test/hooks/usePublicTestCategory';
import { publicQuestionsCount } from 'modules/test/public-question-count';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { TestPaymentDialog } from 'components/portal/TestPaymentDialog';
import { usePortalSubscription } from 'modules/portal-plans/usePortalSubscription';
import { useAuthContext } from 'modules/auth/context';
import { Profile } from 'modules/auth/api';
import { catalogCategories, categoryLabelFor, localizedSubjectLabel } from './catalogCategories';
import { PublicTestCard, LEVELS, LEVEL_LABEL_KEYS, type Level } from './PublicTestCard';
import { SubjectCard } from './SubjectCard';
import { SubjectLevelPanel } from './SubjectLevelPanel';
import { subjectLevel } from './subjectLevel';
import { OTHER_SUBJECT } from 'modules/test/public-subject';
import { CatalogFilterSheet } from './CatalogFilterSheet';
import { portalDialogCloseClass, portalDialogFitClass } from 'components/portal/upsell';

type TestItem = TestModule.Hooks.PublicTestCardItem;
type Access = TestItem['accessType'];

/** Twelve cards: four rows of three on desktop. */
const PAGE_SIZE = 12;
const ACCESS_TYPES: Access[] = ['FREE', 'PAID', 'PLAN'];

const parseLevel = (raw: string | null): Level | undefined =>
  LEVELS.find((level) => level === raw?.toLowerCase());
const parseAccess = (raw: string | null): Access | undefined =>
  ACCESS_TYPES.find((access) => access === raw?.toUpperCase());

/** Phones load the next twelve under the ones already shown; pages are kept per filter set. */
interface PhonePages {
  key: string;
  page: number;
  total: number;
  pages: Record<number, TestItem[]>;
}
const freshPages = (key: string): PhonePages => ({ key, page: 1, total: 0, pages: {} });

interface PublicTestsCatalogProps {
  userBalance: number;
  onBalanceUpdated?: () => void;
  /** False when the page already shows the category pills above the list, so they are not drawn twice. */
  showCategoryChips?: boolean;
}

/** "ingliz-tili" → "Ingliz tili", for a subject the category does not name. */
function humanizeSlug(slug: string): string {
  const words = slug.replace(/[-_]+/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export const PublicTestsCatalog: React.FC<PublicTestsCatalogProps> = ({
  userBalance,
  onBalanceUpdated,
  showCategoryChips = true,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const { methods } = useAuthContext();
  const { data: subscription, refetch: refreshSubscription } = usePortalSubscription();
  const hasActiveSubscription = Boolean(subscription && new Date(subscription.periodEnd).getTime() > Date.now());
  const hasMockAllowance = hasActiveSubscription && (subscription?.mocksRemaining ?? 0) > 0;
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const { data: categoriesResponse } = TestModule.Hooks.usePublicCategories();
  const dynamicCategories = categoriesResponse?.data || [];
  const { selectedCategory, setSelectedCategory } = usePublicTestCategory(dynamicCategories);
  const categories = catalogCategories(dynamicCategories, t, i18n?.language);
  const [params, setParams] = useSearchParams();
  const searchParam = params.get('search') ?? '';
  const subjectParam = params.get('subject') ?? 'all';
  const level = parseLevel(params.get('difficulty'));
  const access = parseAccess(params.get('access'));

  const activeCategory = dynamicCategories.find(
    (c) => c.id.toLowerCase() === selectedCategory.toLowerCase(),
  );
  const activeSubjects = activeCategory?.subjects || [];
  const [searchInput, setSearchInput] = useState(searchParam);
  const lastSyncedParamRef = useRef(searchParam);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const rawPage = Number(params.get('page') ?? 1);
  const page = Number.isSafeInteger(rawPage) && rawPage > 0 ? rawPage : 1;

  const applySearchToParams = useCallback(
    (value: string) => {
      const trimmed = value.trim();
      setParams(
        previous => {
          const current = (previous.get('search') ?? '').trim();
          if (trimmed === current) {
            return previous;
          }
          const next = new URLSearchParams(previous);
          if (trimmed) {
            next.set('search', trimmed);
          } else {
            next.delete('search');
          }
          next.delete('page');
          lastSyncedParamRef.current = trimmed;
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const applySearchRef = useRef(applySearchToParams);
  useEffect(() => {
    applySearchRef.current = applySearchToParams;
  }, [applySearchToParams]);

  // Keep local search input synchronized if URL search param is changed externally
  useEffect(() => {
    if (searchParam !== lastSyncedParamRef.current) {
      lastSyncedParamRef.current = searchParam;
      setSearchInput(searchParam);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }
  }, [searchParam]);

  // Clean up debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    debounceTimerRef.current = setTimeout(() => {
      applySearchRef.current(value);
    }, 400);
  };

  // Enter submits the search form: apply at once instead of waiting for the debounce.
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    applySearchRef.current(searchInput);
  };

  const setFilter = (key: 'difficulty' | 'access', value: string | undefined) =>
    setParams(
      previous => {
        const next = new URLSearchParams(previous);
        if (value) next.set(key, value);
        else next.delete(key);
        next.delete('page');
        return next;
      },
      { replace: true },
    );

  // One update for everything: separate setParams calls in a row would overwrite each other.
  const clearFilters = () => {
    setParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete('category');
        next.delete('subject');
        next.delete('difficulty');
        next.delete('access');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  // Empties the search box at once — the pending debounce must not bring the text back.
  const clearSearchInput = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    lastSyncedParamRef.current = '';
    setSearchInput('');
  };

  // The category setter drops `search` and `subject` from the URL; the box must follow.
  const selectCategory = (category: string) => {
    clearSearchInput();
    setSelectedCategory(category);
  };

  const selectSubject = (subjKey: string) => {
    setParams(
      previous => {
        const next = new URLSearchParams(previous);
        if (subjKey && subjKey !== 'all') {
          next.set('subject', subjKey);
        } else {
          next.delete('subject');
        }
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  // "Tozalash" clears the search and subject too.
  const resetAll = () => {
    clearSearchInput();
    setParams(
      previous => {
        const next = new URLSearchParams(previous);
        next.delete('search');
        next.delete('subject');
        next.delete('difficulty');
        next.delete('access');
        next.delete('page');
        return next;
      },
      { replace: true },
    );
  };

  const setPage = (value: number) => setParams(previous => {
    const next = new URLSearchParams(previous);
    if (value === 1) next.delete('page'); else next.set('page', String(value));
    return next;
  });
  const [selectedPaidTest, setSelectedPaidTest] = useState<TestModule.Hooks.PublicTestCardItem | null>(null);
  const [useSubscriptionForPurchase, setUseSubscriptionForPurchase] = useState(false);
  const [purchasing, setPurchasing] = useState(false);

  // The profile query never re-writes the user after the first load, so the
  // balance the server reports is pushed into the auth state by hand.
  const syncBalance = useCallback((balanceSom: unknown) => {
    if (typeof balanceSom !== 'number' || !Number.isFinite(balanceSom)) return;
    methods.setUser(user => (user ? { ...user, balanceSom } : user));
  }, [methods]);

  // A top-up can land while the page is open, so the amount due is
  // calculated from a fresh balance.
  const paymentTestId = selectedPaidTest && !useSubscriptionForPurchase ? selectedPaidTest.id : null;
  useEffect(() => {
    if (!paymentTestId) return;
    let cancelled = false;
    Profile()
      .then(({ data }) => { if (!cancelled) syncBalance(data.user?.balanceSom); })
      .catch(() => {
        // Keep the last known balance. The purchase endpoint re-checks it.
      });
    return () => { cancelled = true; };
  }, [paymentTestId, syncBalance]);

  const filterKey = [selectedCategory, subjectParam, searchParam.trim(), level ?? '', access ?? ''].join('|');
  const [phone, setPhone] = useState<PhonePages>(() => freshPages(filterKey));
  const phonePages = phone.key === filterKey ? phone : freshPages(filterKey);

  // Subject cards stand in for the test list until a subject is opened or a search is typed. Every test is on
  // a card (tests with no subject on the category's "Other" card), so the list is never the front page.
  const wantsSubjects = subjectParam === 'all' && !searchParam.trim();
  const {
    data: subjectsData,
    isLoading: subjectsLoading,
    isError: subjectsError,
    refetch: refetchSubjects,
  } = TestModule.Hooks.usePublicSubjects({
    params: {
      category: selectedCategory === 'all' ? undefined : selectedCategory,
      difficulty: level,
      accessType: access,
    },
    enabled: wantsSubjects,
  });
  const subjectCards = subjectsData?.data ?? [];
  const showSubjects = wantsSubjects && subjectCards.length > 0;
  const loadingSubjects = wantsSubjects && subjectsLoading;
  const subjectsFailed = wantsSubjects && subjectsError;
  const showList = !wantsSubjects;

  // Only Milliy sertifikat grades a subject; the other categories keep their plain cards.
  const gradesSubjects = selectedCategory.toLowerCase() === 'milliy-sertifikat';
  const { data: subjectProgress } = TestModule.Hooks.useMySubjectProgress(selectedCategory, gradesSubjects);
  const levelOf = (key: string) => (gradesSubjects ? subjectLevel(subjectProgress?.get(key)) : null);

  const { data: publicData, isLoading, isError, isFetching, refetch } = TestModule.Hooks.usePublicTests({
    params: {
      category: selectedCategory === 'all' ? undefined : selectedCategory,
      subject: subjectParam === 'all' ? undefined : subjectParam,
      search: searchParam.trim() || undefined,
      page: isMobile ? phonePages.page : page,
      perPage: PAGE_SIZE,
      difficulty: level,
      accessType: access,
    },
    enabled: showList,
  });

  // Keep each loaded phone page so the next one appends below it.
  useEffect(() => {
    if (!isMobile || !publicData) return;
    setPhone(previous => {
      const base = previous.key === filterKey ? previous : freshPages(filterKey);
      const loaded = publicData.meta.page;
      if (base.pages[loaded] === publicData.items && base.total === publicData.meta.total) return previous;
      return { ...base, total: publicData.meta.total, pages: { ...base.pages, [loaded]: publicData.items } };
    });
  }, [isMobile, publicData, filterKey]);

  const phoneItems = (() => {
    const earlier = Array.from({ length: phonePages.page - 1 }, (_, i) => phonePages.pages[i + 1] ?? []);
    const seen = new Set<string>();
    return [...earlier.flat(), ...(publicData?.items ?? [])].filter(item => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
  })();
  // Cards are the view while no subject is open and nothing is searched: never show list items then.
  const displayItems = !showList ? [] : isMobile ? phoneItems : publicData?.items ?? [];
  const total = publicData?.meta.total ?? (isMobile ? phonePages.total : 0);
  const loadingFirst = isLoading && displayItems.length === 0;
  // What "N tests" counts: every variant under the cards, or the filtered list.
  const shownTotal = showSubjects
    ? subjectCards.reduce((sum, card) => sum + card.variantsCount, 0)
    : showList
      ? publicData?.meta.total
      : undefined;
  const hasFilters = Boolean(
    searchInput.trim() || searchParam.trim() || (subjectParam && subjectParam !== 'all') || level || access,
  );

  const loadMore = () => {
    if (isFetching || !publicData) return;
    setPhone({ ...phonePages, page: phonePages.page + 1 });
  };

  // What the learner already holds on this test. A paid credit only counts for a
  // PAID test; a Plan test's reserved slot still goes through the allowance check.
  const holdings = TestModule.Hooks.useMyTestHoldings();
  const holdingOf = (test: TestModule.Hooks.PublicTestCardItem) => {
    const holding = holdings.data?.get(test.id);
    return holding === 'PURCHASED' && test.accessType !== 'PAID' ? undefined : holding;
  };

  // The subject is only resolved within its category, so both go into the URL together; a new history entry keeps Back on the cards.
  const openSubject = (card: TestModule.Types.PublicSubjectCard) =>
    setParams(previous => {
      const next = new URLSearchParams(previous);
      next.set('category', card.category);
      next.set('subject', card.key);
      next.delete('page');
      return next;
    });
  const inProgressOf = (card: TestModule.Types.PublicSubjectCard) =>
    card.testIds.filter(id => holdings.data?.get(id) === 'IN_PROGRESS').length;

  const handleStartOrBuy = (test: TestModule.Hooks.PublicTestCardItem) => {
    if (purchasing) return;
    // Free, begun or already paid for: the take page resumes or starts it without a charge.
    if (test.accessType === 'FREE' || holdingOf(test)) {
      navigate(`/portal/tests/take/${test.id}`);
      return;
    }
    if (test.accessType === 'PLAN') {
      if (!hasActiveSubscription) {
        setCheckoutOpen(true);
        return;
      }
      if (!hasMockAllowance) {
        // A reserved credit or an in-progress attempt can outlive the allowance.
        // Ask the server before offering a new plan.
        void purchaseAndStart(test, true);
        return;
      }
    }
    setUseSubscriptionForPurchase(test.accessType === 'PLAN');
    setSelectedPaidTest(test);
  };

  const handleStartTest = handleStartOrBuy;

  const purchaseAndStart = async (test: TestModule.Hooks.PublicTestCardItem, useSubscription: boolean) => {
    if (purchasing) return;
    setPurchasing(true);
    try {
      const response = await TestModule.Api.PurchasePublicTest(test.id, useSubscription);
      syncBalance(response?.data?.balanceSom);
      void refreshSubscription();
      toast.success('Test muvaffaqiyatli xarid qilindi!');
      onBalanceUpdated?.();
      setSelectedPaidTest(null);
      navigate(`/portal/tests/take/${test.id}`);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { code?: string; message?: string; balanceSom?: number } } };
      if (e.response?.data?.code === 'PORTAL_MOCK_LIMIT_REACHED' || e.response?.data?.code === 'PORTAL_SUBSCRIPTION_REQUIRED') {
        void refreshSubscription();
        setSelectedPaidTest(null);
        toast.error(t('portal.plan_access_required'));
        setCheckoutOpen(true);
      } else if (e.response?.data?.code === 'INSUFFICIENT_BALANCE') {
        // The dialog stays open and recalculates the amount due from the real balance.
        syncBalance(e.response.data.balanceSom);
        toast.error('Balansda mablag‘ yetarli emas');
      } else {
        toast.error(e.response?.data?.message || 'Xaridni amalga oshirib bo‘lmadi');
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleConfirmPurchase = () => {
    if (selectedPaidTest) void purchaseAndStart(selectedPaidTest, useSubscriptionForPurchase);
  };

  const levelOptions: FilterOption[] = LEVELS.map(value => ({ value, label: t(LEVEL_LABEL_KEYS[value]) }));
  const priceOptions: FilterOption[] = [
    { value: 'FREE', label: t('free') },
    { value: 'PAID', label: t('marketplace_paid_label') },
    { value: 'PLAN', label: t('portal.plan_included') },
  ];

  // Phones keep the page short: the chosen filters sit in one removable row and the sheet picks them.
  const categoryChoices: FilterOption[] = categories.map(({ id, label }) => ({
    value: id,
    label,
    count: dynamicCategories.find((c) => c.id === id)?.testCount,
  }));
  const subjectChoices: FilterOption[] =
    selectedCategory !== 'all'
      ? activeSubjects.map((sub) => ({ value: sub.key, label: localizedSubjectLabel(sub, i18n?.language) }))
      : [];
  const labelOf = (options: FilterOption[], value: string) =>
    options.find((option) => option.value.toLowerCase() === value.toLowerCase())?.label ?? humanizeSlug(value);
  const subjectLabelOf = (value: string) =>
    value === OTHER_SUBJECT ? t('portal_tests_subject_other') : labelOf(subjectChoices, value);
  const activeFilters: Array<{ key: string; label: string; remove: () => void }> = [];
  if (selectedCategory !== 'all') {
    activeFilters.push({ key: 'category', label: labelOf(categoryChoices, selectedCategory), remove: () => selectCategory('all') });
  }
  if (subjectParam !== 'all') {
    activeFilters.push({ key: 'subject', label: subjectLabelOf(subjectParam), remove: () => selectSubject('all') });
  }
  if (level) {
    activeFilters.push({ key: 'level', label: labelOf(levelOptions, level), remove: () => setFilter('difficulty', undefined) });
  }
  if (access) {
    activeFilters.push({ key: 'access', label: labelOf(priceOptions, access), remove: () => setFilter('access', undefined) });
  }

  const renderGrid = () => {
    if (showSubjects) {
      return (
        <div className="grid gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
          {subjectCards.map(card => (
            <SubjectCard
              key={`${card.category}:${card.key}`}
              card={card}
              label={localizedSubjectLabel(card, i18n?.language)}
              categoryLabel={categoryLabelFor(categories, card.category)}
              inProgressCount={inProgressOf(card)}
              onOpen={() => openSubject(card)}
              themed={!showCategoryChips}
              level={levelOf(card.key)}
            />
          ))}
        </div>
      );
    }
    if (loadingFirst || loadingSubjects) {
      return (
        <div className="grid gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex min-h-[244px] flex-col rounded-lg border border-border bg-card p-5">
              <div className="flex items-center justify-between gap-2">
                <Skeleton className="h-[22px] w-20" />
                <Skeleton className="h-3.5 w-16" />
              </div>
              <Skeleton className="mt-4 h-5 w-3/4" />
              <Skeleton className="mt-2 h-3.5 w-full" />
              <Skeleton className="mt-1.5 h-3.5 w-2/3" />
              <div className="mt-auto flex items-center justify-between gap-3 border-t border-border pt-3.5">
                <Skeleton className="h-5 w-16" />
                <Skeleton className="h-9 w-28" />
              </div>
            </div>
          ))}
        </div>
      );
    }
    if (subjectsFailed) {
      return (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={<AlertCircle size={26} />}
            title={t('failed_to_load')}
            secondaryAction={{ label: t('retry'), onClick: () => refetchSubjects() }}
          />
        </div>
      );
    }
    if (showList && isError && displayItems.length === 0) {
      return (
        <div className="rounded-lg border border-border bg-card">
          <EmptyState
            icon={<AlertCircle size={26} />}
            title={t('failed_to_load')}
            secondaryAction={{ label: t('retry'), onClick: () => refetch() }}
          />
        </div>
      );
    }
    if (displayItems.length === 0) {
      return (
        <div className="rounded-lg border border-border bg-card">
          {hasFilters ? (
            <EmptyState
              icon={<Search size={26} />}
              title={t('portal.no_search_tests')}
              description={t('portal.try_other_search')}
              secondaryAction={{ label: t('clear_filters'), onClick: resetAll }}
            />
          ) : (
            <EmptyState
              icon={<ClipboardList size={26} />}
              title={t('portal.tests_adding_soon')}
              description={t('portal.check_back_soon')}
            />
          )}
        </div>
      );
    }
    return (
      <div className="grid gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3">
        {displayItems.map((test) => {
          const holding = holdingOf(test);
          // A Plan test already begun outlives the allowance, so it is never locked.
          const isLocked = test.accessType === 'PLAN' && !hasMockAllowance && holding !== 'IN_PROGRESS';
          return (
            <PublicTestCard
              key={test.id}
              test={test}
              categoryLabel={categoryLabelFor(categories, test.category)}
              holding={holding}
              lockedReason={
                isLocked
                  ? t(hasActiveSubscription ? 'portal.plan_limit_reached' : 'portal.plan_access_required')
                  : undefined
              }
              // A priced card waits for what the learner holds, so it never offers a price already paid.
              disabled={purchasing || (holdings.isLoading && test.accessType !== 'FREE')}
              onAction={() => handleStartTest(test)}
            />
          );
        })}
      </div>
    );
  };

  const searchField = (
    <form role="search" onSubmit={handleSearchSubmit} className={isMobile ? 'min-w-0 flex-1' : 'w-full sm:w-80'}>
      {/* The shared field is 36px; the portal's controls are 40px (44px on phones). */}
      <TableSearch
        value={searchInput}
        onChange={handleSearchChange}
        placeholder={t('portal_tests_search_placeholder')}
        aria-label={t('portal_tests_search_placeholder')}
        className={cn('sm:w-full', isMobile ? '[&_input]:h-11' : '[&_input]:h-11 lg:[&_input]:h-10')}
      />
    </form>
  );

  return (
    <div className="flex flex-col gap-5">
      {checkoutOpen && <PlanCheckoutModal open onOpenChange={setCheckoutOpen} subscription={subscription} />}

      <div className="flex flex-col gap-3">
        {isMobile ? (
          <>
            <div className="flex gap-2">
              {searchField}
              <button
                type="button"
                aria-haspopup="dialog"
                aria-expanded={filtersOpen}
                onClick={() => setFiltersOpen(true)}
                className="inline-flex h-11 shrink-0 items-center gap-2 rounded-md border border-foreground bg-foreground px-3.5 text-[15px] font-semibold text-background transition-colors hover:bg-foreground/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <ListFilter size={18} aria-hidden="true" />
                {t('filters')}
                {activeFilters.length > 0 && (
                  <span className="inline-flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-primary px-1.5 text-xs font-bold tabular-nums text-primary-foreground">
                    {activeFilters.length}
                  </span>
                )}
              </button>
            </div>

            {(activeFilters.length > 0 || showCategoryChips) && (
            <div
              role="group"
              aria-label={activeFilters.length > 0 ? t('filters') : t('portal_tests_categories')}
              className={cn(
                'flex gap-2',
                // Applied filters wrap so "Tozalash" is never cut off; the category chips scroll under a fade.
                activeFilters.length > 0
                  ? 'flex-wrap'
                  : 'no-scrollbar -mx-4 overflow-x-auto px-4 [-webkit-mask-image:linear-gradient(90deg,#000_88%,transparent)] [mask-image:linear-gradient(90deg,#000_88%,transparent)]',
              )}
            >
              {activeFilters.length > 0 ? (
                <>
                  {activeFilters.map(({ key, label, remove }) => (
                    <button
                      key={key}
                      type="button"
                      aria-label={t('portal_tests_remove_filter', { name: label })}
                      onClick={remove}
                      className="inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-foreground bg-primary pl-3.5 pr-2.5 text-[13.5px] font-semibold text-primary-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      {label}
                      <X size={15} aria-hidden="true" />
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="h-11 shrink-0 px-2 text-[13.5px] font-semibold text-foreground underline underline-offset-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    {t('clear')}
                  </button>
                </>
              ) : (
                categories
                  .filter(({ id }) => id !== 'all')
                  .map(({ id, label }) => (
                    <button
                      key={id}
                      type="button"
                      onClick={() => selectCategory(id)}
                      className="inline-flex h-11 shrink-0 items-center whitespace-nowrap rounded-full border border-border bg-card px-4 text-[13.5px] font-medium text-foreground/80 transition-colors hover:border-foreground/20 hover:bg-muted/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      {label}
                    </button>
                  ))
              )}
            </div>
            )}
          </>
        ) : (
          <>
            {showCategoryChips && (
            <div role="group" aria-label={t('portal_tests_categories')} className="flex flex-wrap gap-2">
              {categories.map((cat) => {
                const active = selectedCategory.toLowerCase() === cat.id.toLowerCase();
                return (
                  <button
                    key={cat.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => selectCategory(cat.id)}
                    className={cn(
                      'inline-flex h-9 shrink-0 items-center gap-[7px] whitespace-nowrap rounded-full border px-3.5 text-[13.5px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                      active
                        ? 'border-foreground bg-foreground text-background'
                        : 'border-border bg-card text-foreground/80 hover:border-foreground/20 hover:bg-muted/40 hover:text-foreground',
                    )}
                  >
                    {cat.label}
                  </button>
                );
              })}
            </div>
            )}

            {/* Dynamic Subjects row when category is selected */}
            {selectedCategory !== 'all' && activeSubjects.length > 0 && (
              <div role="group" aria-label={t('portal_tests_subjects')} className="flex flex-wrap items-center gap-1.5 pt-0.5">
                <button
                  type="button"
                  aria-pressed={subjectParam === 'all'}
                  onClick={() => selectSubject('all')}
                  className={cn(
                    'inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    subjectParam === 'all'
                      ? 'border-foreground bg-foreground font-semibold text-background shadow-xs'
                      : 'border-border bg-card font-medium text-muted-foreground hover:border-foreground/20 hover:bg-muted/40 hover:text-foreground',
                  )}
                >
                  {t('all', 'Barchasi')}
                </button>
                {activeSubjects.map((sub) => {
                  const isActive = subjectParam.toLowerCase() === sub.key.toLowerCase();
                  return (
                    <button
                      key={sub.key}
                      type="button"
                      aria-pressed={isActive}
                      onClick={() => selectSubject(sub.key)}
                      className={cn(
                        'inline-flex h-7 shrink-0 items-center whitespace-nowrap rounded-full border px-3 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        isActive
                          ? 'border-foreground bg-foreground font-semibold text-background shadow-xs'
                          : 'border-border bg-card font-medium text-muted-foreground hover:border-foreground/20 hover:bg-muted/40 hover:text-foreground',
                      )}
                    >
                      {localizedSubjectLabel(sub, i18n?.language)}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2">
              {searchField}
              <FilterButton
                label={t('portal_tests_filter_level')}
                options={levelOptions}
                value={level}
                onChange={(value) => setFilter('difficulty', value)}
                className="h-11 lg:h-10"
              />
              <FilterButton
                label={t('portal_tests_filter_price')}
                options={priceOptions}
                value={access}
                onChange={(value) => setFilter('access', value)}
                className="h-11 lg:h-10"
              />
              {hasFilters && <FilterReset onClick={resetAll} className="h-11 lg:h-10" />}
              {shownTotal !== undefined && (
                <span className="ml-auto whitespace-nowrap text-[13.5px] text-muted-foreground">
                  <span className="font-semibold tabular-nums text-foreground">{shownTotal}</span>{' '}
                  {t('pager_noun_tests')}
                </span>
              )}
            </div>
          </>
        )}
      </div>

      {showList && subjectParam !== 'all' && (
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="h-11 gap-1.5 px-2 lg:h-9" onClick={() => selectSubject('all')}>
            <ArrowLeft size={16} aria-hidden="true" />
            {t('portal_tests_all_subjects')}
          </Button>
          <h2 className="font-display text-[17px] font-bold tracking-[-0.01em] text-foreground">
            {subjectLabelOf(subjectParam)}
          </h2>
        </div>
      )}

      {showList && subjectParam !== 'all' && levelOf(subjectParam) ? (
        <div className="flex flex-col gap-4 lg:grid lg:grid-cols-[minmax(0,1fr)_300px] lg:items-start">
          <SubjectLevelPanel subject={subjectLabelOf(subjectParam)} progress={levelOf(subjectParam)!} className="order-first lg:order-last" />
          <div className="min-w-0">{renderGrid()}</div>
        </div>
      ) : (
        renderGrid()
      )}

      {!showList ? null : isMobile ? (
        !loadingFirst && displayItems.length > 0 && (
          isError ? (
            // The next page failed: keep what is shown and offer the same page again.
            <Button variant="outline" size="lg" className="w-full" onClick={() => refetch()}>
              {t('retry')}
            </Button>
          ) : (
            <LoadMore compact shown={displayItems.length} total={total} step={PAGE_SIZE} loading={isFetching} onLoadMore={loadMore} />
          )
        )
      ) : (
        !isLoading && !isError && publicData && (
          <TablePager
            page={page}
            pageCount={publicData.meta.totalPages || 1}
            pageSize={PAGE_SIZE}
            total={publicData.meta.total}
            onPageChange={setPage}
            noun={t('pager_noun_tests')}
            className="mt-2 border-t border-border py-4"
          />
        )
      )}

      <CatalogFilterSheet
        open={isMobile && filtersOpen}
        onClose={() => setFiltersOpen(false)}
        categoryOptions={categoryChoices}
        category={selectedCategory}
        subjectOptions={subjectChoices}
        subject={subjectParam === 'all' ? undefined : subjectParam}
        levelOptions={levelOptions}
        priceOptions={priceOptions}
        level={level}
        price={access}
        onCategoryChange={selectCategory}
        onSubjectChange={(value) => selectSubject(value ?? 'all')}
        onLevelChange={(value) => setFilter('difficulty', value)}
        onPriceChange={(value) => setFilter('access', value)}
        onClear={clearFilters}
        total={shownTotal ?? total}
      />

      {selectedPaidTest && !useSubscriptionForPurchase && (
        <TestPaymentDialog
          test={{
            id: selectedPaidTest.id,
            title: selectedPaidTest.title,
            priceSom: selectedPaidTest.priceSom,
            questionsCount: publicQuestionsCount(selectedPaidTest),
            durationMinutes: selectedPaidTest.durationMinutes,
            sectionsCount: selectedPaidTest.sectionsCount,
          }}
          balanceSom={userBalance}
          purchasing={purchasing}
          onConfirm={handleConfirmPurchase}
          onBalanceChanged={syncBalance}
          onClose={() => setSelectedPaidTest(null)}
        />
      )}
      {selectedPaidTest && useSubscriptionForPurchase && (
        <Dialog open onOpenChange={() => setSelectedPaidTest(null)}>
          <DialogContent closeClassName={portalDialogCloseClass}
          className={cn(portalDialogFitClass, 'max-w-md')}>
            <DialogHeader>
              <DialogTitle className="text-lg font-bold">Imtihonni boshlash</DialogTitle>
              <DialogDescription className="text-xs">
                {selectedPaidTest.title}
              </DialogDescription>
            </DialogHeader>

            <p className="my-3 rounded-lg bg-muted/40 p-4 text-sm">
              Obunangizdan 1 ta test ishlatiladi. Balansingizdan pul yechilmaydi.
              Qolgan testlar: {subscription?.mocksRemaining} ta.
            </p>

            <DialogFooter className="mt-4 gap-2">
              <Button
                variant="outline"
                onClick={() => setSelectedPaidTest(null)}
                disabled={purchasing}
              >
                Bekor qilish
              </Button>
              <Button onClick={handleConfirmPurchase} disabled={purchasing}>
                {purchasing ? 'Yechilmoqda...' : 'Tasdiqlash va Boshlash'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default PublicTestsCatalog;
